"use client";

import { createContext, useContext, useCallback, useEffect } from "react";
import { useWakeLock } from "react-screen-wake-lock";
import { logger } from "@/lib/logger";

interface WakeLockContextValue {
  requestWakeLock: () => void;
  releaseWakeLock: () => void;
  isSupported: boolean;
  isActive: boolean;
}

const WakeLockContext = createContext<WakeLockContextValue | null>(null);

/**
 * WakeLockProvider manages screen wake lock throughout the interview lifecycle.
 *
 * Exposes requestWakeLock() and releaseWakeLock() functions to be called on user actions:
 * - Request on: "Start Interview", "Continue as Guest", "Unmute", "Resume"
 * - Release on: "Pause", "Mute", "Complete", "Leave Interview"
 */
export function WakeLockProvider({ children }: { children: React.ReactNode }) {
  const { isSupported, request, release, released } = useWakeLock({
    onRequest: () => logger.wakeLock("Requested"),
    onError: (error) => logger.wakeLock("Error", error),
    onRelease: () => logger.wakeLock("Released"),
  });

  const requestWakeLock = useCallback(() => {
    if (!isSupported) {
      logger.wakeLock("Wake Lock API not supported");
      return;
    }

    // Always try to request - browser releases wake lock on navigation
    // and library state might not be in sync
    logger.wakeLock("Requesting wake lock", { released });
    request();
  }, [isSupported, released, request]);

  const releaseWakeLock = useCallback(() => {
    if (isSupported && !released) {
      logger.wakeLock("User action triggered release");
      release();
    }
  }, [isSupported, released, release]);

  // Release wake lock on unmount (user closes tab/browser)
  useEffect(() => {
    return () => {
      if (isSupported && !released) {
        logger.wakeLock("Component unmounting, releasing wake lock");
        release();
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const value: WakeLockContextValue = {
    requestWakeLock,
    releaseWakeLock,
    isSupported,
    isActive: !released,
  };

  return (
    <WakeLockContext.Provider value={value}>
      {children}
    </WakeLockContext.Provider>
  );
}

/**
 * Hook to access wake lock controls
 */
export function useWakeLockControl() {
  const context = useContext(WakeLockContext);
  if (!context) {
    throw new Error("useWakeLockControl must be used within WakeLockProvider");
  }
  return context;
}
